/**
 * useAgreementPdf Hook
 *
 * Manages debounced PDF generation and signed-document upload logic
 * extracted from ReviewSign and AgreementSign.
 *
 * Responsibilities:
 * - Generate a filled agreement PDF (debounced, cancellable)
 * - Expose blob URL for iframe preview
 * - Generate final PDF bytes for upload
 * - Upload signed PDF + reference docs to S3
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { fillAgreementPdf, fillAgreementPdfBytes } from '../utils/fillAgreementPdf';
import { uploadSignedDocument, S3Document } from '../services/agreementService';

export interface AgreementPdfParams {
  partyName: string;
  partyAbn?: string;
  partyAcn?: string;
  partyAddress?: string;
  partyEmail?: string;
  agreementDate: string;
  signatoryName?: string;
  drawnSignature?: string;
  signatureMode?: 'typed' | 'drawn';
  ackTermsOfUse?: boolean;
  ackPrivacyPolicy?: boolean;
  ackServicesAgreement?: boolean;
  ackAuthorisedToSign?: boolean;
}

export interface UseAgreementPdfReturn {
  /** Blob URL for the filled PDF (use in iframe src) */
  filledPdfUrl: string | null;
  /** Whether the PDF is still loading */
  isPdfLoading: boolean;
  /** Generate final signed PDF bytes for upload */
  generateSignedPdfBytes: (params: AgreementPdfParams) => Promise<Uint8Array>;
  /** Upload signed PDF + optional reference docs to S3. Returns S3 key. */
  uploadSignedPdf: (
    pdfBytes: Uint8Array,
    referenceDocs?: S3Document[]
  ) => Promise<{ s3Key?: string; base64?: string }>;
}

export function useAgreementPdf(params: AgreementPdfParams, debounceMs = 300): UseAgreementPdfReturn {
  const [filledPdfUrl, setFilledPdfUrl] = useState<string | null>(null);
  const [isPdfLoading, setIsPdfLoading] = useState(true);
  const prevBlobUrl = useRef<string | null>(null);

  // Debounced PDF preview generation
  useEffect(() => {
    let cancelled = false;
    setIsPdfLoading(true);

    const generatePdf = async () => {
      try {
        const blobUrl = await fillAgreementPdf(params);
        if (!cancelled) {
          // Revoke previous blob
          if (prevBlobUrl.current) URL.revokeObjectURL(prevBlobUrl.current);
          prevBlobUrl.current = blobUrl;
          setFilledPdfUrl(blobUrl);
          setIsPdfLoading(false);
        } else if (blobUrl) {
          URL.revokeObjectURL(blobUrl);
        }
      } catch {
        if (!cancelled) {
          setFilledPdfUrl(null);
          setIsPdfLoading(false);
        }
      }
    };

    const timer = setTimeout(generatePdf, debounceMs);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    params.partyName,
    params.partyAbn,
    params.partyAcn,
    params.partyAddress,
    params.partyEmail,
    params.agreementDate,
    params.signatoryName,
    params.drawnSignature,
    params.signatureMode,
    params.ackTermsOfUse,
    params.ackPrivacyPolicy,
    params.ackServicesAgreement,
    params.ackAuthorisedToSign,
    debounceMs,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (prevBlobUrl.current) URL.revokeObjectURL(prevBlobUrl.current);
    };
  }, []);

  const generateSignedPdfBytes = useCallback(
    async (overrideParams: AgreementPdfParams): Promise<Uint8Array> => {
      return fillAgreementPdfBytes(overrideParams);
    },
    []
  );

  const uploadSignedPdf = useCallback(
    async (
      pdfBytes: Uint8Array,
      referenceDocs?: S3Document[]
    ): Promise<{ s3Key?: string; base64?: string }> => {
      let s3Key: string | undefined;
      let base64: string | undefined;

      const sessionToken = localStorage.getItem('reg_session_token') || '';

      if (sessionToken) {
        // Upload the signed agreement
        const key = await uploadSignedDocument('signed_agreement.pdf', pdfBytes, sessionToken);
        if (key) s3Key = key;

        // Upload reference docs (T&Cs, Privacy, etc.)
        if (referenceDocs?.length) {
          for (const doc of referenceDocs) {
            try {
              const res = await fetch(doc.downloadUrl);
              if (!res.ok) throw new Error(`Failed to fetch ${doc.fileName}: ${res.status}`);
              const buf = await res.arrayBuffer();
              await uploadSignedDocument(`signed_${doc.fileName}`, new Uint8Array(buf), sessionToken);
            } catch {
              // Non-blocking
            }
          }
        }
      }

      // Base64 fallback
      let binary = '';
      for (let i = 0; i < pdfBytes.length; i++) binary += String.fromCharCode(pdfBytes[i]);
      base64 = btoa(binary);

      return { s3Key, base64 };
    },
    []
  );

  return {
    filledPdfUrl,
    isPdfLoading,
    generateSignedPdfBytes,
    uploadSignedPdf,
  };
}
